import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, MessageSquare, Radio, Smartphone, SlidersHorizontal } from "lucide-react";

const CHANNELS = [
  { id: "WABA", label: "WhatsApp (WABA)", icon: MessageSquare },
  { id: "RCS", label: "RCS", icon: Radio },
  { id: "SMS", label: "SMS", icon: Smartphone },
];
const SEGMENTS = [
  { id: "school_admins", label: "All School Admins" },
  { id: "educators", label: "All Educators" },
  { id: "creators", label: "All Content Creators / Channel Owners" },
  { id: "vendors", label: "All Store Vendors" },
  { id: "students", label: "All Tenant Students" },
  { id: "open_students", label: "All Open School Students" },
];
const VARS = ["{name}", "{school_name}", "{date}"];

function Composer({ channel }) {
  const [segments, setSegments] = useState([]);
  const [message, setMessage] = useState("");
  const [schedule, setSchedule] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSegments(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const insertVar = (v) => setMessage(m => (m + " " + v).trim());
  const sendNow = async () => {
    if (!segments.length) { toast.error("Pick at least one audience segment"); return; }
    if (!message.trim()) { toast.error("Message is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/notifications/broadcast", { channel, message, segments });
      toast.success(`${channel} broadcast queued → ${r.data.recipient_count} recipient(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed"); }
    setBusy(false);
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="nb-card p-5 bg-[#C4B5FD] space-y-2">
        <h3 className="display text-lg font-bold">Target Audience</h3>
        {SEGMENTS.map(s => (
          <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`gn-seg-${s.id}`}>
            <input type="checkbox" checked={segments.includes(s.id)} onChange={() => toggle(s.id)} />
            {s.label}
          </label>
        ))}
        <p className="text-[11px] opacity-70 pt-1">Tenant / Open students support cascading Country → Board → Grade filters (applied at send-time).</p>
      </div>
      <div className="nb-card p-5 bg-white space-y-3">
        <h3 className="display text-lg font-bold">Message · {channel}</h3>
        <div className="flex gap-1 flex-wrap">
          {VARS.map(v => <button key={v} onClick={() => insertVar(v)} className="nb-badge bg-[#FDE047] text-xs" data-testid={`gn-var-${v}`}>{v}</button>)}
        </div>
        <textarea className="nb-textarea" rows={5} placeholder={channel === "SMS" ? "SMS text (DLT template)" : `Approved ${channel} template or text`} value={message} onChange={e => setMessage(e.target.value)} data-testid="gn-message" />
        <div className="flex items-center gap-2">
          <input type="datetime-local" className="nb-input text-sm" value={schedule} onChange={e => setSchedule(e.target.value)} data-testid="gn-schedule" />
          <span className="text-xs opacity-60">optional schedule</span>
        </div>
        <button className="nb-btn w-full justify-center" onClick={sendNow} disabled={busy} data-testid="gn-send"><Send size={16} /> {busy ? "Sending…" : "Send Broadcast Now"}</button>
      </div>
    </div>
  );
}

function ProviderControls() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/notifications/channels-all").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setChan = async (school_id, channels) => {
    try { await api.put(`/notifications/channels/${school_id}`, { channels }); toast.success("Updated"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const shown = rows.filter(r => r.name?.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="nb-card p-4 bg-[#FDE047] text-sm">
        Provider config: <b>MSG91</b> (India) & <b>Twilio</b> (Global) keys live in <b>Platform Settings</b> (masked). Sending is in <b>stub mode</b> until keys + template/DLT approvals are added.
      </div>
      <input className="nb-input max-w-sm" placeholder="Search partner schools…" value={q} onChange={e => setQ(e.target.value)} data-testid="gn-school-search" />
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>School</th><th>Plan</th><th>WABA</th><th>RCS</th><th>SMS</th></tr></thead>
          <tbody>
            {shown.map(s => (
              <tr key={s.school_id} data-testid={`gn-school-${s.school_id}`}>
                <td className="font-bold">{s.name}<div className="text-xs opacity-60">{[s.district, s.state].filter(Boolean).join(", ")}</div></td>
                <td className="text-xs mono">{s.plan_key || "—"}</td>
                {["WABA", "RCS", "SMS"].map(ch => (
                  <td key={ch}>
                    <button onClick={() => setChan(s.school_id, { ...s.channels, [ch]: !s.channels[ch] })}
                      className={`nb-badge ${s.channels[ch] ? "bg-[#86EFAC]" : "bg-[#FCA5A5]"}`} data-testid={`gn-toggle-${s.school_id}-${ch}`}>
                      {s.channels[ch] ? "On" : "Off"}
                    </button>
                  </td>
                ))}
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No schools</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── WABA hub (approved-template broadcasts + per-school permission controls) ──
// ── Master Channel Controls & Consumption (pinned platform row + per-school) ──
const PRIMARY_OPTS = ["WhatsApp Only", "SMS Only", "RCS Only", "All Channels (Simultaneous)"];
const CHAN_KEYS = ["WhatsApp", "SMS", "RCS"];

function MasterChannelControls() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/waba/consumption").then(r => setRows(r.data.rows || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const patch = async (school_id, body) => {
    setBusy(school_id);
    try { await api.put(`/waba/schools/${school_id}/config`, body); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    setBusy("");
  };
  const toggleChan = (row, ch) => {
    const ec = { ...(row.enabled_channels || {}), [ch]: !((row.enabled_channels || {})[ch]) };
    patch(row.school_id, { enabled_channels: ec });
  };
  const wa = (c) => `${c?.whatsapp?.auto ?? 0} auto / ${c?.whatsapp?.manual ?? 0} manual`;
  const sms = (c) => (c?.sms?.total > 0) ? `${c.sms.auto} auto / ${c.sms.manual} manual` : "N/A";

  return (
    <div className="nb-card p-0 overflow-hidden" data-testid="master-channel-controls">
      <div className="p-3 bg-[#FDE047] font-bold text-sm flex items-center gap-2">
        <SlidersHorizontal size={16} /> Master Channel Controls & Consumption
      </div>
      <div className="overflow-x-auto">
        <table className="nb-table text-sm" style={{ minWidth: 1000 }}>
          <thead><tr>
            <th>School</th><th>Plan</th><th>Enabled Channels</th><th>Active Primary</th>
            <th>WhatsApp Consumed</th><th>SMS Consumed</th><th>RCS</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {rows.map(row => row.is_master ? (
              <tr key="__platform__" className="bg-[#E0E7FF] font-bold" data-testid="mcc-master-row">
                <td>{row.name}</td>
                <td><span className="nb-badge bg-black text-white text-[10px]">{row.plan_name}</span></td>
                <td className="text-xs">All Channels Active</td>
                <td className="text-xs">Global Master</td>
                <td className="text-xs" data-testid="mcc-master-wa">{row.consumption?.whatsapp?.total ?? 0} broadcasts</td>
                <td className="text-xs">{row.consumption?.sms?.total ?? 0}</td>
                <td className="text-xs">{row.consumption?.rcs?.total ?? 0}</td>
                <td className="text-xs opacity-60">—</td>
              </tr>
            ) : (
              <tr key={row.school_id} data-testid={`mcc-row-${row.school_id}`}>
                <td className="font-bold">{row.name}</td>
                <td><span className="nb-badge bg-white text-[10px]" data-testid={`mcc-plan-${row.school_id}`}>{row.plan_name}</span></td>
                <td>
                  <div className="flex gap-2">
                    {CHAN_KEYS.map(ch => (
                      <label key={ch} className="flex items-center gap-1 text-[11px] cursor-pointer" title={ch}>
                        <input type="checkbox" checked={!!(row.enabled_channels || {})[ch]}
                          onChange={() => toggleChan(row, ch)} disabled={busy === row.school_id}
                          data-testid={`mcc-chan-${row.school_id}-${ch}`} />
                        {ch === "WhatsApp" ? "WA" : ch}
                      </label>
                    ))}
                  </div>
                </td>
                <td>
                  <select className="nb-select text-xs py-1" value={row.active_primary || "SMS Only"}
                    onChange={e => setPrimaryFor(patch, row, e.target.value)} disabled={busy === row.school_id}
                    data-testid={`mcc-primary-${row.school_id}`}>
                    {PRIMARY_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </td>
                <td className="text-xs" data-testid={`mcc-wa-${row.school_id}`}>{wa(row.consumption)}</td>
                <td className="text-xs" data-testid={`mcc-sms-${row.school_id}`} title={row.consumption?.sms?.total > 0 ? "" : "SMS logging pending provider hook"}>{sms(row.consumption)}</td>
                <td className="text-xs">{row.consumption?.rcs?.total ?? 0}</td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    <button className={`nb-badge text-[10px] ${row.automated_allowed ? "bg-[#86EFAC]" : "bg-[#FCA5A5]"}`}
                      onClick={() => patch(row.school_id, { automated_allowed: !row.automated_allowed })}
                      data-testid={`mcc-automated-${row.school_id}`}>Auto {row.automated_allowed ? "On" : "Off"}</button>
                    <button className={`nb-badge text-[10px] ${row.school_admin_messaging ? "bg-[#86EFAC]" : "bg-[#FCA5A5]"}`}
                      onClick={() => patch(row.school_id, { school_admin_messaging: !row.school_admin_messaging })}
                      data-testid={`mcc-adminmsg-${row.school_id}`}>Admin {row.school_admin_messaging ? "On" : "Off"}</button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-500">No schools</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="p-2 text-[11px] opacity-70">
        "SMS Only" is the default for existing schools to protect live SMS. Switch to "WhatsApp Only" or "All Channels" to activate WABA. "All Channels" fires WhatsApp + a single SMS (no duplication).
      </div>
    </div>
  );
}

function setPrimaryFor(patch, row, v) { patch(row.school_id, { active_primary: v }); }

const LOG_FILTERS = [
  { id: "all", label: "All" }, { id: "failed", label: "Failed Only" },
  { id: "automated", label: "Automated" }, { id: "manual", label: "Manual" },
  { id: "bulk", label: "Bulk Summaries" },
];

function PlatformWabaLogs() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = (f) => api.get(`/waba/logs/all?filter=${f}`).then(r => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(filter); }, [filter]);
  const badge = (l) => {
    const ds = l.delivery_status || (l.status || "").toLowerCase();
    const m = { delivered: ["bg-[#86EFAC]", "Delivered"], sent: ["bg-[#93C5FD]", "Sent"], simulated: ["bg-[#93C5FD]", "Sent"], failed: ["bg-[#FCA5A5]", "Failed"], failed_waba: ["bg-[#FCA5A5]", "Failed"], queued: ["bg-[#FDE047]", "Queued"] };
    const [c, t] = m[ds] || ["bg-slate-200", l.status || "—"];
    return <span className={`nb-badge ${c}`} title={t === "Failed" ? (l.error_message || "") : ""}>{t}</span>;
  };
  return (
    <div className="nb-card p-0 overflow-hidden" data-testid="platform-waba-logs">
      <div className="p-3 bg-[#C4B5FD] font-bold text-sm flex items-center justify-between gap-2 flex-wrap">
        <span>Platform Live WABA Dispatch Log</span>
        <select className="nb-select text-xs py-1 w-auto" value={filter} onChange={e => setFilter(e.target.value)} data-testid="platform-waba-log-filter">
          {LOG_FILTERS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
      </div>
      <div className="max-h-[55vh] overflow-auto">
        <table className="nb-table text-sm">
          <thead><tr><th>When</th><th>School</th><th>Mobile / Target</th><th>Template</th><th>Trigger</th><th>Status</th><th>Failure Reason</th></tr></thead>
          <tbody>
            {rows.map(l => {
              const when = l.created_at || l.timestamp;
              const isBulk = l.dispatch_type === "bulk_summary";
              return (
                <tr key={l.id} data-testid={`platform-waba-log-${l.id}`}>
                  <td className="mono text-xs">{when ? new Date(when).toLocaleString() : "—"}</td>
                  <td className="text-xs font-bold">{l.school_name}</td>
                  <td className="mono text-xs">{isBulk ? <em>{l.bulk_total_count} recip.</em> : (l.recipient_phone || l.to || "—")}</td>
                  <td className="text-xs">{isBulk ? <b>Bulk — {l.bulk_total_count} via {l.display_name || l.template}</b> : (l.display_name || l.template)}</td>
                  <td><span className="nb-badge bg-white text-[10px]">{l.trigger_type || l.kind}</span></td>
                  <td>{badge(l)}</td>
                  <td className="text-xs text-rose-700">{(l.delivery_status === "failed") ? (l.error_message || "Failed") : ""}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No WABA dispatches for this filter</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WabaHub() {
  const [allTemplates, setAllTemplates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [schools, setSchools] = useState([]);
  const [tpl, setTpl] = useState("");
  const [vals, setVals] = useState({});
  const [media, setMedia] = useState({ link: "", filename: "" });
  const [target, setTarget] = useState("");
  const [grade, setGrade] = useState("");
  const [busy, setBusy] = useState(false);
  const [blackout, setBlackout] = useState([]);
  const [recharges, setRecharges] = useState([]);
  const [quickTest, setQuickTest] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [lmsGrades, setLmsGrades] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [rechargeApprove, setRechargeApprove] = useState(null);

  const load = () => {
    api.get("/waba/templates").then(r => { setAllTemplates(r.data); setTemplates(r.data.filter(t => t.audience === "global_admin" && t.active)); }).catch(() => {});
    api.get("/waba/schools").then(r => setSchools(r.data)).catch(() => {});
    api.get("/waba/platform-settings").then(r => setBlackout(r.data.blackout_dates || [])).catch(() => {});
    api.get("/waba/recharge-requests").then(r => setRecharges(r.data)).catch(() => {});
    api.get("/waba/lms-grades").then(r => setLmsGrades(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const spec = templates.find(t => t.name === tpl);
  const setVal = (k, v) => setVals(s => ({ ...s, [k]: v }));

  const sendTest = async () => {
    if (!spec || !testTo.trim()) { toast.error("Pick a template and enter a test number"); return; }
    setBusy(true);
    try {
      const body = tpl === "ponderr_new_quiz_announcement"
        ? [vals.student_name || "", vals.subject || "", vals.quiz_title || "", vals.question_count || "", vals.deadline_str || ""]
        : spec.body.map(f => vals[f] || "");
      const button_urls = spec.button > 0 ? [vals[spec.button_param_name] || vals.quiz_id || ""] : [];
      const r = await api.post("/waba/global/quick-test", { template: tpl, to: testTo.trim(), body, media: spec.header ? media : undefined, button_urls });
      toast.success(`Test ${r.data.simulated ? "SIMULATED (key not set)" : "sent"} → ${testTo}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    setBusy(false);
  };

  const send = async () => {
    if (!spec) { toast.error("Pick a template"); return; }
    setBusy(true);
    try {
      const payload = { template: tpl, media: spec.header ? media : undefined,
        school_id: target || undefined, grades: (grade || "").split(",").filter(Boolean) };
      if (tpl === "ponderr_new_quiz_announcement") {
        Object.assign(payload, { subject: vals.subject, quiz_title: vals.quiz_title,
          question_count: vals.question_count, deadline_str: vals.deadline_str, quiz_id: vals.quiz_id });
      } else {
        payload.body = spec.body.map(f => vals[f] || "");
      }
      const r = await api.post("/waba/global/broadcast", payload);
      toast.success(`Queued → ${r.data.total_recipients} recipient(s)${r.data.schools?.[0]?.simulated ? " (SIMULATED — key not set)" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    setBusy(false);
  };

  const saveTemplate = async () => {
    try {
      await api.patch(`/waba/templates/${editRow.slug}`, {
        display_name: editRow.display_name, active: editRow.active,
        meta_slug: (editRow.meta_slug || "").trim(),
        param_names: (editRow.param_names_str || "").split(",").map(s => s.trim()).filter(Boolean) });
      toast.success("Template updated"); setEditRow(null); load();
    } catch { toast.error("Failed"); }
  };

  const toggleCfg = async (school_id, cfg, key) => {
    try { await api.put(`/waba/schools/${school_id}/config`, { [key]: !cfg[key] }); load(); }
    catch { toast.error("Failed"); }
  };
  const addBlackout = () => setBlackout(b => [...b, { start: "", end: "", label: "" }]);
  const saveBlackout = async () => {
    try { await api.put("/waba/platform-settings", { blackout_dates: blackout.filter(b => b.start) }); toast.success("Blackout saved"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="nb-card p-5 bg-white space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="display text-lg font-bold">Global Broadcast · Approved Template</h3>
            <label className="flex items-center gap-1 text-xs font-bold cursor-pointer" data-testid="waba-quicktest-toggle">
              <input type="checkbox" checked={quickTest} onChange={e => setQuickTest(e.target.checked)} /> Quick Test Mode
            </label>
          </div>
          <select className="nb-select" value={tpl} onChange={e => { setTpl(e.target.value); setVals({}); }} data-testid="waba-template-select">
            <option value="">Select a template…</option>
            {templates.map(t => <option key={t.name} value={t.name}>{t.display_name || t.name}</option>)}
          </select>
          {spec && (
            <>
              {spec.header && (
                <div className="grid grid-cols-2 gap-2">
                  <input className="nb-input text-sm" placeholder={`${spec.header} URL (https…)`} value={media.link} onChange={e => setMedia({ ...media, link: e.target.value })} data-testid="waba-media-link" />
                  {spec.header === "document" && <input className="nb-input text-sm" placeholder="filename.pdf" value={media.filename} onChange={e => setMedia({ ...media, filename: e.target.value })} />}
                </div>
              )}
              {(tpl === "ponderr_new_quiz_announcement"
                ? ["student_name", "subject", "quiz_title", "question_count", "deadline_str", "quiz_id"]
                : spec.body).map((f, i) => (
                  f === "target_grades" ? (
                    <div key={f} data-testid="waba-var-target_grades">
                      <label className="nb-label">Target Grades (from Ponderr LMS)</label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {lmsGrades.length === 0 && <span className="text-xs opacity-60">No LMS grades configured</span>}
                        {lmsGrades.map(g => {
                          const sel = (vals.target_grades || "").split(",").filter(Boolean);
                          const on = sel.includes(g);
                          return <button key={g} type="button" onClick={() => { const ns = on ? sel.filter(x => x !== g) : [...sel, g]; setVal("target_grades", ns.join(",")); }}
                            className={`nb-badge ${on ? "bg-[#86EFAC]" : "bg-white"}`} data-testid={`waba-grade-${g}`}>{g}</button>;
                        })}
                      </div>
                    </div>
                  ) : (
                    <input key={f} className="nb-input text-sm" placeholder={`{{${i + 1}}} ${f}`} value={vals[f] || ""} onChange={e => setVal(f, e.target.value)} data-testid={`waba-var-${f}`} />)
                ))}
              {quickTest ? (
                <>
                  <input className="nb-input text-sm" placeholder="Test Mobile Number (e.g. 919XXXXXXXXX)" value={testTo} onChange={e => setTestTo(e.target.value)} data-testid="waba-test-number" />
                  <button className="nb-btn w-full justify-center nb-btn-tertiary" onClick={sendTest} disabled={busy} data-testid="waba-send-test"><Send size={16} /> {busy ? "Sending…" : "Send Test Message"}</button>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-2">
                    <input className="nb-input text-sm" placeholder="Limit to school_id (optional)" value={target} onChange={e => setTarget(e.target.value)} />
                    <div>
                      <label className="nb-label">Audience Grades (from Ponderr LMS · blank = all)</label>
                      <div className="flex flex-wrap gap-1 mt-1" data-testid="waba-audience-grades">
                        {lmsGrades.length === 0 && <span className="text-xs opacity-60">No LMS grades configured</span>}
                        {lmsGrades.map(g => {
                          const on = (grade || "").split(",").filter(Boolean).includes(g);
                          return <button key={g} type="button" data-testid={`waba-audience-grade-${g}`}
                            onClick={() => { const sel = (grade || "").split(",").filter(Boolean); const ns = on ? sel.filter(x => x !== g) : [...sel, g]; setGrade(ns.join(",")); }}
                            className={`nb-badge ${on ? "bg-[#86EFAC]" : "bg-white"}`}>{g}</button>;
                        })}
                      </div>
                    </div>
                  </div>
                  <button className="nb-btn w-full justify-center" onClick={send} disabled={busy} data-testid="waba-send"><Send size={16} /> {busy ? "Sending…" : "Send Broadcast"}</button>
                </>
              )}
            </>
          )}
        </div>
        <div className="nb-card p-5 bg-[#C4B5FD] space-y-2">
          <h3 className="display text-lg font-bold">Preview</h3>
          {!spec && <p className="text-sm opacity-70">Select a template to preview variables.</p>}
          {spec && (
            <div className="space-y-1 text-sm" data-testid="waba-preview">
              <div className="nb-badge bg-white">{spec.category} · header: {spec.header || "none"}</div>
              {spec.header && <div className="text-xs mono break-all">HEADER {spec.header}: {media.link || "—"}</div>}
              {(tpl === "ponderr_new_quiz_announcement" ? ["subject", "quiz_title", "question_count", "deadline_str"] : spec.body).map((f, i) => (
                <div key={f}><b>{`{{${i + 1}}}`}</b> {f}: <span className="opacity-80">{vals[f] || <em>empty</em>}</span></div>
              ))}
              {spec.button > 0 && <div className="text-xs">Button URL param: {tpl === "ponderr_new_quiz_announcement" ? (vals.quiz_id || "quiz_id") : "auto"}</div>}
            </div>
          )}
        </div>
      </div>

      <div className="nb-card p-0 overflow-hidden" data-testid="waba-templates-table">
        <div className="p-3 bg-[#C4B5FD] font-bold text-sm">Registered Templates (database-backed · upsert-seeded)</div>
        <table className="nb-table text-sm">
          <thead><tr><th>Slug</th><th>Display Name</th><th>Category</th><th>Header</th><th>Params</th><th>Active</th><th>Actions</th></tr></thead>
          <tbody>
            {allTemplates.map(t => (
              <tr key={t.slug} data-testid={`waba-tpl-${t.slug}`}>
                <td className="mono text-xs">{t.slug}</td>
                <td className="font-bold">{t.display_name}</td>
                <td><span className="nb-badge bg-white text-[10px]">{t.audience}</span></td>
                <td className="text-xs">{t.header || "none"}</td>
                <td className="text-xs">{(t.body || []).join(", ")}</td>
                <td><span className={`nb-badge ${t.active ? "bg-[#86EFAC]" : "bg-[#FCA5A5]"}`}>{t.active ? "Yes" : "No"}</span></td>
                <td><button className="nb-btn nb-btn-ghost px-2 py-1 text-xs" data-testid={`waba-tpl-edit-${t.slug}`}
                  onClick={() => setEditRow({ slug: t.slug, display_name: t.display_name, active: t.active, meta_slug: t.meta_slug || "", param_names_str: (t.body || []).join(", ") })}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editRow && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50" onClick={() => setEditRow(null)}>
          <div className="nb-card p-6 bg-white w-full max-w-md space-y-3" onClick={e => e.stopPropagation()} data-testid="waba-tpl-edit-modal">
            <h3 className="display text-lg font-bold">Edit · {editRow.slug}</h3>
            <div><label className="nb-label">Display Name</label>
              <input className="nb-input mt-1" value={editRow.display_name} onChange={e => setEditRow({ ...editRow, display_name: e.target.value })} data-testid="waba-tpl-edit-name" /></div>
            <div><label className="nb-label">Param Names (comma-separated)</label>
              <input className="nb-input mt-1" value={editRow.param_names_str} onChange={e => setEditRow({ ...editRow, param_names_str: e.target.value })} /></div>
            <div><label className="nb-label">Meta Slug (exact name registered in Meta WABA)</label>
              <input className="nb-input mt-1" value={editRow.meta_slug || ""} placeholder="leave blank to use the key above"
                onChange={e => setEditRow({ ...editRow, meta_slug: e.target.value })} data-testid="waba-tpl-edit-meta-slug" />
              <p className="text-[11px] opacity-60 mt-1">The outgoing WhatsApp template name sent to MSG91. Map it to your approved Meta slug if it differs from the system key.</p></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editRow.active} onChange={e => setEditRow({ ...editRow, active: e.target.checked })} data-testid="waba-tpl-edit-active" /> Active</label>
            <div className="flex gap-2"><button className="nb-btn flex-1 justify-center" onClick={saveTemplate} data-testid="waba-tpl-save">Save</button>
              <button className="nb-btn nb-btn-ghost" onClick={() => setEditRow(null)}>Cancel</button></div>
          </div>
        </div>
      )}


      <MasterChannelControls />

      <PlatformWabaLogs />

      <div className="nb-card p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="display text-lg font-bold">Platform Blackout Windows</h3>
          <div className="flex gap-2">
            <button className="nb-btn nb-btn-ghost text-xs" onClick={addBlackout} data-testid="waba-add-blackout">+ Window</button>
            <button className="nb-btn text-xs" onClick={saveBlackout} data-testid="waba-save-blackout">Save</button>
          </div>
        </div>
        <p className="text-xs opacity-70">Suppresses ALL automated fee/attendance dispatches during the window.</p>
        {blackout.map((b, i) => (
          <div key={i} className="grid grid-cols-3 gap-2">
            <input type="date" className="nb-input text-sm" value={b.start || ""} onChange={e => setBlackout(x => x.map((y, j) => j === i ? { ...y, start: e.target.value } : y))} />
            <input type="date" className="nb-input text-sm" value={b.end || ""} onChange={e => setBlackout(x => x.map((y, j) => j === i ? { ...y, end: e.target.value } : y))} />
            <input className="nb-input text-sm" placeholder="label" value={b.label || ""} onChange={e => setBlackout(x => x.map((y, j) => j === i ? { ...y, label: e.target.value } : y))} />
          </div>
        ))}
      </div>

      <div className="nb-card p-4" data-testid="recharge-inbox">
        <h3 className="display text-lg font-bold mb-2">Recharge Requests</h3>
        {recharges.length === 0 ? <p className="text-sm opacity-70">No requests.</p> : (
          <table className="nb-table"><thead><tr><th>School</th><th>Requested / Notes</th><th>Requested At</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>{recharges.map(r => (
              <tr key={r.id} data-testid={`recharge-row-${r.id}`}>
                <td className="text-xs mono" data-testid={`recharge-row-school-${r.id}`}>{r.school_id}</td>
                <td className="text-xs">{r.requested_units || "—"} · {r.note}</td>
                <td className="text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td><span className={`nb-badge ${r.status === "approved" ? "bg-[#86EFAC]" : r.status === "rejected" ? "bg-[#FCA5A5]" : "bg-[#FDE047]"}`}>{r.status}{r.approved_units ? ` (+${r.approved_units})` : ""}</span></td>
                <td>{r.status === "new" || r.status === "pending"
                  ? <button className="nb-btn nb-btn-secondary px-2 py-1 text-xs" data-testid={`recharge-approve-${r.id}`}
                      onClick={() => setRechargeApprove({ id: r.id, units: r.requested_units || 1000 })}>Approve &amp; Credit</button>
                  : <span className="text-xs opacity-50">—</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
      {rechargeApprove && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50" onClick={() => setRechargeApprove(null)}>
          <div className="nb-card p-6 bg-white w-full max-w-md space-y-3" onClick={e => e.stopPropagation()} data-testid="recharge-approve-modal">
            <h3 className="display text-lg font-bold">Approve &amp; Credit</h3>
            <label className="nb-label">Units to Add</label>
            <input type="number" className="nb-input" value={rechargeApprove.units} onChange={e => setRechargeApprove({ ...rechargeApprove, units: e.target.value })} data-testid="recharge-units-input" />
            <div className="flex gap-2">
              <button className="nb-btn flex-1 justify-center" data-testid="recharge-credit-confirm"
                onClick={async () => { try { const r = await api.post(`/waba/recharge-requests/${rechargeApprove.id}/approve`, { units: Number(rechargeApprove.units) }); toast.success(`Credited +${rechargeApprove.units}. New allocation ${r.data.quota.total_allocated}`); setRechargeApprove(null); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } }}>Credit Units</button>
              <button className="nb-btn nb-btn-ghost" onClick={() => setRechargeApprove(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function GlobalNotifications() {
  const [tab, setTab] = useState("waba");
  const CH = CHANNELS.filter(c => c.id !== "WABA"); // WABA now lives in the primary hub
  const TABS = [{ id: "waba", label: "WABA Templates & Controls", icon: MessageSquare }, ...CH];
  return (
    <div className="space-y-4">
      <div>
        <h2 className="display text-2xl font-bold">Notifications · Broadcast Hub</h2>
        <p className="text-sm opacity-70">WhatsApp (WABA) primary hub · RCS & SMS legacy composers. MSG91 (India) / Twilio (Global).</p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`gn-tab-${t.id}`}
            className={`nb-btn text-sm ${tab === t.id ? "" : "nb-btn-ghost"}`}><t.icon size={14} /> {t.label}</button>
        ))}
      </div>
      {tab === "waba" ? <WabaHub /> : <Composer channel={tab} />}
    </div>
  );
}
